import type { BadgeTone } from '@shared/ui';

import { rowMessage } from './row-warnings';

import type { TenantOutcome, TenantRow } from '../schemas/bulk-migration';

/**
 * How an outcome reads, as plain data so vitest (node, no DOM) can see it.
 *
 * The words are an operator's, not the runner's: `planned` is what the dry-run
 * calls a tenant it would migrate, and "Migratable" is what someone choosing
 * whom to migrate is looking for.
 */

export const OUTCOME_LABEL: Readonly<Record<TenantOutcome, string>> = {
    pending: 'Waiting',
    planned: 'Migratable',
    not_migrated: 'Not migratable',
    launched: 'Launched',
    blocked: 'Blocked',
    running: 'Running',
    completed: 'Completed',
    completed_with_warnings: 'Completed with warnings',
    failed: 'Failed',
    skipped: 'Skipped',
    error: 'Error',
};

export const OUTCOME_TONE: Readonly<Record<TenantOutcome, BadgeTone>> = {
    pending: 'neutral',
    planned: 'success',
    not_migrated: 'warning',
    launched: 'info',
    blocked: 'warning',
    running: 'info',
    completed: 'success',
    completed_with_warnings: 'warning',
    failed: 'danger',
    skipped: 'neutral',
    error: 'danger',
};

/** The runner's skip reasons, decided before any Salesforce read. */
const SKIP_REASON_COPY: Readonly<Record<string, string>> = {
    unknown_tenant: 'not a client of this environment',
    sandbox_excluded: 'sandbox, excluded from this analysis',
    sin_org: 'no Salesforce org connected',
    duplicado: 'its Salesforce org belongs to another client too',
    flag_off: 'the module is turned off for this client',
};

/** A reason code in words: a known skip, else the code itself, which is still searchable. */
export function reasonCopy(reason: string | null): string | null {
    if (reason === null || reason.length === 0) return null;
    return SKIP_REASON_COPY[reason] ?? reason;
}

/**
 * Just the why, or `null` when the outcome needs none (planned, completed…).
 * The pre-run check findings the message ends with are not part of it: the
 * table shows them on their own lines (`rowMessage`), for every outcome.
 */
export function outcomeReason(
    row: Pick<TenantRow, 'outcome' | 'reason' | 'message' | 'warnings'>,
): string | null {
    if (row.outcome === 'skipped') return reasonCopy(row.reason);
    if (
        row.outcome === 'error' ||
        row.outcome === 'not_migrated' ||
        row.outcome === 'blocked' ||
        row.outcome === 'failed'
    ) {
        return rowMessage(row).text ?? reasonCopy(row.reason);
    }
    return null;
}

export interface OutcomeFilterOption {
    readonly value: string;
    readonly label: string;
}

/**
 * The outcome filter, built from the job's own counts so it only offers what
 * exists. Skips are split by reason (`skipped:<reason>`), because "skipped"
 * alone mixes a sandbox the operator excluded with an org nobody connected.
 */
export function outcomeFilterOptions(
    counts: Readonly<Record<string, number>>,
): OutcomeFilterOption[] {
    const options: OutcomeFilterOption[] = [{ value: 'all', label: 'All outcomes' }];
    const known = new Set<string>(Object.keys(OUTCOME_LABEL));

    for (const [key, count] of Object.entries(counts).sort(([a], [b]) => a.localeCompare(b))) {
        if (count <= 0) continue;
        const [outcome, reason] = key.split(':');
        if (outcome === undefined || !known.has(outcome)) continue;
        const label = OUTCOME_LABEL[outcome as TenantOutcome];
        options.push({
            value: key,
            label: `${reason ? `${label} — ${reasonCopy(reason) ?? reason}` : label} (${String(count)})`,
        });
    }
    return options;
}

/**
 * The tiles' numbers: every non-skip outcome as is, and every skip folded into
 * one `skipped`, whatever its reason.
 */
export function outcomeTotals(
    counts: Readonly<Record<string, number>>,
): Map<TenantOutcome, number> {
    const totals = new Map<TenantOutcome, number>();
    for (const [key, count] of Object.entries(counts)) {
        const outcome = key.split(':')[0];
        if (outcome === undefined || !(outcome in OUTCOME_LABEL)) continue;
        const typed = outcome as TenantOutcome;
        totals.set(typed, (totals.get(typed) ?? 0) + count);
    }
    return totals;
}
